// src/pages/kpis/compras/DisponibilidadMP.jsx
// KPI de Disponibilidad de Materia Prima — pestañas Resumen / Histórico / Detalle.
//
// Reglas del KPI (acordadas con Compras):
//  - % = promedio del % surtido de cada línea de orden de traslado hacia
//    Almacén Producción con fecha del mes.
//    En la práctica las líneas se reciben completas o no se reciben.
//  - Una línea "Cerrada" sin recibir (no se tenía el producto)
//    SÍ cuenta como incumplimiento; en el detalle lleva leyenda.
//  - El mes en curso es parcial: se muestra aparte y no se compara contra la meta.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as XLSX from 'xlsx';
import { FiDownload, FiRefreshCw, FiInfo, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../../../services/api';
import s from '../../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/kpis/disponibilidad-mp';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690' };
const LEYENDA_CERRADA = 'Cerrada sin recibir — no se tenía el producto';

// ─── Helpers ───
const num = (v) => (v === null || v === undefined ? null : parseFloat(v));
const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-MX', { maximumFractionDigits: 2 }));
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');
const nombreMes = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
const nombreMesCorto = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
const pad = (n) => String(n).padStart(2, '0');

/** Rango YYYY-MM-DD de un mes a partir de la fecha "mes" de la vista */
function rangoDeMes(mes) {
  const d = new Date(mes);
  const y = d.getFullYear();
  const m = d.getMonth();
  const ultimo = new Date(y, m + 1, 0).getDate();
  return { desde: `${y}-${pad(m + 1)}-01`, hasta: `${y}-${pad(m + 1)}-${pad(ultimo)}` };
}

function semaforo(valor, meta) {
  if (valor === null || meta === null) return 'gris';
  if (valor >= meta) return 'verde';
  if (valor >= meta - 5) return 'amarillo';
  return 'rojo';
}

function estadoRecepcion(fila) {
  if (fila.cerrada_sin_recibir) return 'cerrada';
  const p = num(fila.pct_completado) || 0;
  if (p >= 100) return 'completo';
  if (p > 0) return 'parcial';
  return 'pendiente';
}

// ─── Gráfica (Chart.js directo, sin wrapper) ───
function Grafica({ config, alto = 280 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return (
    <div style={{ position: 'relative', height: alto }}>
      <canvas ref={ref} />
    </div>
  );
}

export default function DisponibilidadMP() {
  const [tab, setTab] = useState('resumen');
  const [resumen, setResumen] = useState([]);
  const [meta, setMeta] = useState(95);
  const [ultimaSync, setUltimaSync] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Detalle
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', recepcion: '', busqueda: '' });
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [detalle, setDetalle] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [exportando, setExportando] = useState(false);

  async function cargarResumen() {
    const res = await api.get(`${BASE}/resumen`);
    setResumen(res.data.data);
    if (res.data.meta !== null && res.data.meta !== undefined) setMeta(res.data.meta);
    setUltimaSync(res.data.ultimaSync);
    return res.data.data;
  }

  async function cargarDetalle(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoDetalle(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/detalle`, { params });
      setDetalle({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el detalle. Revisa la conexión e intenta de nuevo.' });
    } finally {
      setCargandoDetalle(false);
    }
  }

  // Carga inicial: resumen + detalle del último mes cerrado
  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const filas = await cargarResumen();
        const cerrado = filas.find((r) => !r.es_parcial) || filas[0];
        const f = cerrado ? { ...filtros, ...rangoDeMes(cerrado.mes) } : filtros;
        setFiltros(f);
        await cargarDetalle(f, 1, porPagina);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de Disponibilidad de Materia Prima.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      const res = await api.post('/api/kpis/sync/disponibilidad-mp');
      await cargarResumen();
      await cargarDetalle();
      const r = res.data.resultado;
      setMensaje({ tipo: 'ok', texto: `Sincronizado con NetSuite: ${r.insertados} líneas nuevas, ${r.actualizados} actualizadas.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo sincronizar con NetSuite.' });
    } finally {
      setSincronizando(false);
    }
  }

  // ─── Datos derivados ───
  const ordenAsc = useMemo(() => [...resumen].reverse(), [resumen]);
  const mesCerrado = resumen.find((r) => !r.es_parcial) || null;
  const idxCerrado = mesCerrado ? resumen.indexOf(mesCerrado) : -1;
  const mesPrevioCerrado = idxCerrado >= 0 ? resumen.slice(idxCerrado + 1).find((r) => !r.es_parcial) : null;
  const mesEnCurso = resumen.find((r) => r.es_parcial) || null;

  function irADetalleDeMes(mes) {
    const f = { ...filtros, ...rangoDeMes(mes), recepcion: '' };
    setFiltros(f);
    setPagina(1);
    setTab('detalle');
    cargarDetalle(f, 1, porPagina);
  }

  // ─── Excel ───
  function descargarHistorico() {
    const filas = ordenAsc.map((r) => ({
      Mes: nombreMes(r.mes) + (r.es_parcial ? ' (parcial)' : ''),
      '% Disponibilidad': num(r.pct_completado_promedio),
      Meta: meta,
      'Llegó a la meta': r.es_parcial ? 'En curso' : num(r.pct_completado_promedio) >= meta ? 'Sí' : 'No',
      'Líneas': Number(r.total_lineas),
      'Completas': Number(r.lineas_completas),
      'Parciales': Number(r.lineas_parciales),
      'Sin recibir': Number(r.lineas_sin_recibir),
      'De ellas, cerradas sin recibir': Number(r.lineas_cerradas_sin_recibir),
      'Órdenes de traslado': Number(r.total_documentos),
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
    XLSX.writeFile(wb, 'Disponibilidad_MP_historico.xlsx');
  }

  async function descargarDetalle() {
    try {
      setExportando(true);
      const params = { exportar: '1' };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/detalle`, { params });
      const filas = res.data.data.map((f) => ({
        Documento: f.numero_documento,
        Fecha: fmtFecha(f.fecha),
        'Artículo': f.articulo,
        Nota: f.nota || '',
        Cantidad: num(f.cantidad),
        Recibido: num(f.cantidad_recibida),
        '% Completado': num(f.pct_completado),
        'Estado NetSuite': f.estado || '',
        Observación: f.cerrada_sin_recibir ? LEYENDA_CERRADA : '',
      }));
      const ws = XLSX.utils.json_to_sheet(filas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
      const sufijo = filtros.desde && filtros.hasta ? `_${filtros.desde}_a_${filtros.hasta}` : '';
      XLSX.writeFile(wb, `Disponibilidad_MP_detalle${sufijo}.xlsx`);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo generar el Excel del detalle.' });
    } finally {
      setExportando(false);
    }
  }

  // ─── Configs de gráficas (memo para no recrear el canvas en cada render) ───
  const configLinea = useMemo(() => ({
    type: 'line',
    plugins: [ChartDataLabels],
    data: {
      labels: ordenAsc.map((r) => nombreMesCorto(r.mes) + (r.es_parcial ? '*' : '')),
      datasets: [
        {
          label: '% Disponibilidad',
          data: ordenAsc.map((r) => num(r.pct_completado_promedio)),
          borderColor: COLOR.morado,
          backgroundColor: COLOR.morado,
          pointBackgroundColor: ordenAsc.map((r) => (r.es_parcial ? '#fff' : COLOR[semaforo(num(r.pct_completado_promedio), meta)])),
          pointBorderColor: ordenAsc.map((r) => (r.es_parcial ? COLOR.gris : COLOR[semaforo(num(r.pct_completado_promedio), meta)])),
          pointRadius: 5,
          tension: 0.25,
          segment: { borderDash: (ctx) => (ordenAsc[ctx.p1DataIndex]?.es_parcial ? [5, 4] : undefined) },
          datalabels: { align: 'top', color: '#3A2E42', font: { size: 10, weight: 600 }, formatter: (v) => `${v}%` },
        },
        {
          label: `Meta (${meta}%)`,
          data: ordenAsc.map(() => meta),
          borderColor: COLOR.verde,
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          datalabels: { display: false },
        },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { suggestedMin: 80, max: 102, ticks: { callback: (v) => `${v}%` } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [ordenAsc, meta]);

  const configBarras = useMemo(() => ({
    type: 'bar',
    data: {
      labels: ordenAsc.map((r) => nombreMesCorto(r.mes) + (r.es_parcial ? '*' : '')),
      datasets: [
        { label: 'Completas', data: ordenAsc.map((r) => Number(r.lineas_completas)), backgroundColor: COLOR.verde },
        { label: 'Parciales', data: ordenAsc.map((r) => Number(r.lineas_parciales)), backgroundColor: COLOR.amarillo },
        { label: 'Pendientes de recibir', data: ordenAsc.map((r) => Number(r.lineas_sin_recibir) - Number(r.lineas_cerradas_sin_recibir)), backgroundColor: COLOR.lila },
        { label: 'Cerradas sin recibir', data: ordenAsc.map((r) => Number(r.lineas_cerradas_sin_recibir)), backgroundColor: COLOR.rojo },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { x: { stacked: true }, y: { stacked: true, title: { display: true, text: 'Líneas de traslado' } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [ordenAsc]);

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Disponibilidad de Materia Prima</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>
            Última sincronización con NetSuite: {ultimaSync ? new Date(ultimaSync).toLocaleString('es-MX') : 'sin registro'}
          </div>
        </div>
        <button onClick={handleSincronizar} disabled={sincronizando} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> {sincronizando ? 'Sincronizando...' : 'Sincronizar con NetSuite'}
        </button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['detalle', 'Detalle']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          <div style={s.kgrid}>
            {mesCerrado ? (
              <TarjetaMesCerrado fila={mesCerrado} previo={mesPrevioCerrado} meta={meta} onVerDetalle={() => irADetalleDeMes(mesCerrado.mes)} />
            ) : (
              <div style={s.kcard}><div style={s.kSub}>Aún no hay un mes cerrado.</div></div>
            )}
            {mesEnCurso && (
              <TarjetaMesEnCurso fila={mesEnCurso} referencia={mesCerrado} onVerDetalle={() => irADetalleDeMes(mesEnCurso.mes)} />
            )}
          </div>

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Promedio del % recibido de cada línea de las órdenes de traslado de materia prima hacia Almacén Producción, con fecha del mes.
                En la práctica cada línea se recibe completa o no se recibe, así que equivale al % de líneas recibidas completas.
                Las líneas cerradas sin recibir (no se tenía el producto) cuentan como no cumplidas.
                Meta: {meta}% o más.
                <br />
                <strong style={{ color: '#2A1830' }}>De dónde sale.</strong> NetSuite, búsqueda guardada "Resultados Disponibilidad Materia Prima Dasavena" (customsearch728). Cada sincronización actualiza el mes en curso y los dos anteriores.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>% de disponibilidad por mes</div>
            <Grafica config={configLinea} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Mes en curso, todavía parcial (línea punteada).</div>
          </div>

          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Líneas de traslado por estado de recepción</div>
            <Grafica config={configBarras} />
          </div>

          <div style={{ ...s.headerFila, margin: '18px 0 8px' }}>
            <h3 style={s.subtituloLista}>Tabla mensual</h3>
            <button onClick={descargarHistorico} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> Descargar Excel
            </button>
          </div>
          <div style={s.subtitulo}>Haz clic en un mes para ver sus líneas en Detalle.</div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Mes</th>
                  <th style={s.th}>% Disp.</th>
                  <th style={s.th}>Meta</th>
                  <th style={s.th}>Líneas</th>
                  <th style={s.th}>Completas</th>
                  <th style={s.th}>Parciales</th>
                  <th style={s.th}>Sin recibir</th>
                  <th style={s.th}>Cerradas s/ recibir</th>
                  <th style={s.th}>Órdenes</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r) => {
                  const v = num(r.pct_completado_promedio);
                  const color = r.es_parcial ? COLOR.gris : COLOR[semaforo(v, meta)];
                  return (
                    <tr key={r.mes} onClick={() => irADetalleDeMes(r.mes)} style={{ cursor: 'pointer' }}>
                      <td style={s.td}>
                        {nombreMes(r.mes)}
                        {r.es_parcial && <span style={x.tagParcial}>parcial</span>}
                      </td>
                      <td style={{ ...s.td, fontWeight: 700, color }}>{fmtPct(v)}</td>
                      <td style={s.td}>{r.es_parcial ? 'En curso' : v >= meta ? 'Cumplió' : 'No cumplió'}</td>
                      <td style={s.td}>{r.total_lineas}</td>
                      <td style={s.td}>{r.lineas_completas}</td>
                      <td style={s.td}>{r.lineas_parciales}</td>
                      <td style={s.td}>{r.lineas_sin_recibir}</td>
                      <td style={{ ...s.td, color: Number(r.lineas_cerradas_sin_recibir) > 0 ? COLOR.rojo : undefined }}>{r.lineas_cerradas_sin_recibir}</td>
                      <td style={s.td}>{r.total_documentos}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ DETALLE ═══════════ */}
      {tab === 'detalle' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select
                style={s.filtroInput}
                value={resumen.find((r) => { const g = rangoDeMes(r.mes); return g.desde === filtros.desde && g.hasta === filtros.hasta; })?.mes || ''}
                onChange={(e) => {
                  const f = e.target.value ? { ...filtros, ...rangoDeMes(e.target.value) } : { ...filtros, desde: '', hasta: '' };
                  setFiltros(f);
                }}
              >
                <option value="">Rango personalizado</option>
                {resumen.map((r) => (
                  <option key={r.mes} value={r.mes}>{nombreMes(r.mes)}{r.es_parcial ? ' (parcial)' : ''}</option>
                ))}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Recepción</span>
              <select style={s.filtroInput} value={filtros.recepcion} onChange={(e) => setFiltros({ ...filtros, recepcion: e.target.value })}>
                <option value="">Todas</option>
                <option value="completo">Recibidas completas</option>
                <option value="parcial">Parciales</option>
                <option value="sin_recibir">Sin recibir (incluye cerradas)</option>
                <option value="cerrada_sin_recibir">Solo cerradas sin recibir</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input
                type="text"
                placeholder="Documento o artículo"
                style={{ ...s.filtroInput, minWidth: 180 }}
                value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarDetalle(filtros, 1); } }}
              />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarDetalle(filtros, 1); }}>Buscar</button>
            <button
              style={s.linkChico}
              onClick={() => { const f = { desde: '', hasta: '', recepcion: '', busqueda: '' }; setFiltros(f); setPagina(1); cargarDetalle(f, 1); }}
            >
              Limpiar filtros
            </button>
          </div>

          <div style={{ ...s.headerFila, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>
              {cargandoDetalle ? 'Cargando...' : `${detalle.total.toLocaleString('es-MX')} líneas`}
              {filtros.desde && filtros.hasta && ` del ${fmtFecha(filtros.desde + 'T00:00:00')} al ${fmtFecha(filtros.hasta + 'T00:00:00')}`}
            </div>
            <button onClick={descargarDetalle} disabled={exportando || detalle.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Documento</th>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>Artículo</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Cantidad</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Recibido</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% Compl.</th>
                  <th style={s.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoDetalle && detalle.data.length === 0 && (
                  <tr><td colSpan={7} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay líneas con estos filtros. Cambia el mes o limpia los filtros.</td></tr>
                )}
                {detalle.data.map((f, i) => {
                  const est = estadoRecepcion(f);
                  return (
                    <tr key={`${f.numero_documento}-${f.id_linea ?? `i${i}`}`} style={est === 'cerrada' ? x.filaCerrada : est === 'pendiente' ? x.filaPendiente : undefined}>
                      <td style={s.td}>{f.numero_documento}</td>
                      <td style={s.td}>{fmtFecha(f.fecha)}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 200 }}>{f.articulo}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(f.cantidad)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(f.cantidad_recibida)}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: est === 'completo' ? COLOR.verde : est === 'parcial' ? COLOR.amarillo : COLOR.rojo }}>
                        {fmtPct(f.pct_completado)}
                      </td>
                      <td style={{ ...s.td, whiteSpace: 'normal' }}>
                        {est === 'cerrada' ? <span style={x.tagCerrada}>{LEYENDA_CERRADA}</span> : <span style={{ color: COLOR.gris }}>{f.estado || '—'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select
                style={s.inputChico}
                value={porPagina}
                onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarDetalle(filtros, 1, pp); }}
              >
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                style={x.botonPagina}
                disabled={pagina <= 1 || cargandoDetalle}
                onClick={() => { const p = pagina - 1; setPagina(p); cargarDetalle(filtros, p); }}
                aria-label="Página anterior"
              >
                <FiChevronLeft />
              </button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {detalle.totalPaginas}</span>
              <button
                style={x.botonPagina}
                disabled={pagina >= detalle.totalPaginas || cargandoDetalle}
                onClick={() => { const p = pagina + 1; setPagina(p); cargarDetalle(filtros, p); }}
                aria-label="Página siguiente"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tarjetas del Resumen ───
function TarjetaMesCerrado({ fila, previo, meta, onVerDetalle }) {
  const v = num(fila.pct_completado_promedio);
  const sem = semaforo(v, meta);
  const cumplio = v >= meta;
  const diffMeta = v - meta;
  const vPrevio = previo ? num(previo.pct_completado_promedio) : null;
  const diffPrevio = vPrevio !== null ? v - vPrevio : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR[sem]}` }}>
      <div style={s.kLbl}>Mes cerrado: {nombreMes(fila.mes)}</div>
      <div style={{ ...s.kVal, fontSize: 32, color: COLOR[sem] }}>{fmtPct(v)}</div>
      <div style={{ ...x.veredicto, color: cumplio ? COLOR.verde : COLOR.rojo }}>
        {cumplio ? `Cumplió la meta de ${meta}%` : `No cumplió la meta de ${meta}% (${diffMeta.toFixed(2)} pts)`}
      </div>
      <div style={s.kSub}>
        {fila.lineas_completas} de {fila.total_lineas} líneas recibidas completas en {fila.total_documentos} órdenes de traslado.
        {Number(fila.lineas_sin_recibir) > 0 && ` ${fila.lineas_sin_recibir} sin recibir`}
        {Number(fila.lineas_cerradas_sin_recibir) > 0 && `, de ellas ${fila.lineas_cerradas_sin_recibir} cerradas porque no se tenía el producto`}
        {Number(fila.lineas_sin_recibir) > 0 && '.'}
      </div>
      {diffPrevio !== null && (
        <div style={s.kSub}>
          {diffPrevio >= 0 ? '▲' : '▼'} {Math.abs(diffPrevio).toFixed(2)} pts vs {nombreMes(previo.mes)} ({fmtPct(vPrevio)})
        </div>
      )}
      <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={onVerDetalle}>Ver líneas del mes</button>
    </div>
  );
}

function TarjetaMesEnCurso({ fila, referencia, onVerDetalle }) {
  const v = num(fila.pct_completado_promedio);
  const vRef = referencia ? num(referencia.pct_completado_promedio) : null;
  const diff = vRef !== null ? v - vRef : null;
  const hoy = new Date();
  const diasMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
  return (
    <div style={{ ...s.kcard, borderTop: `3px dashed ${COLOR.gris}` }}>
      <div style={s.kLbl}>
        En curso: {nombreMes(fila.mes)} <span style={x.tagParcial}>parcial, día {hoy.getDate()} de {diasMes}</span>
      </div>
      <div style={{ ...s.kVal, fontSize: 32, color: '#2A1830' }}>{fmtPct(v)}</div>
      {diff !== null && (
        <div style={{ ...x.veredicto, color: COLOR.gris }}>
          {diff >= 0 ? '▲' : '▼'} {Math.abs(diff).toFixed(2)} pts vs {nombreMes(referencia.mes)}
        </div>
      )}
      <div style={s.kSub}>
        {fila.lineas_completas} de {fila.total_lineas} líneas recibidas hasta hoy; {fila.lineas_sin_recibir} todavía pendientes.
        Los traslados recientes aún no se surten, por eso el mes en curso no se compara contra la meta.
      </div>
      <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={onVerDetalle}>Ver líneas del mes</button>
    </div>
  );
}

// Estilos propios de esta página (lo compartido viene de estilos.js)
const x = {
  veredicto: { fontSize: 13, fontWeight: 600, margin: '6px 0 6px' },
  tagParcial: { fontSize: 10, fontWeight: 600, color: '#7a5417', background: '#F9F0DD', padding: '2px 8px', borderRadius: 10, marginLeft: 6, textTransform: 'none', letterSpacing: 0 },
  tagCerrada: { fontSize: 11, fontWeight: 600, color: '#B23A48', background: '#F7E5E7', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' },
  filaCerrada: { background: '#FDF3F1' },
  filaPendiente: { background: '#FBF8F3' },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
};